import React from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isSafeRedirect } from "@/lib/safeRedirect";

export function ProtectedRoute({ children, roles }) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div
                    data-testid="auth-loading"
                    className="text-sm text-slate-500 tracking-wide uppercase"
                >
                    Loading…
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (user.must_reset_password && location.pathname !== "/change-password") {
        return <Navigate to="/change-password" replace />;
    }

    if (roles && roles.length > 0 && !roles.includes(user.role)) {
        const fallback =
            user.role === "customer" ? "/customer"
                : user.role === "manager" ? "/manager"
                    : "/admin";
        return <Navigate to={fallback} replace />;
    }

    return children;
}

export function PublicOnlyRoute({ children }) {
    const { user, loading } = useAuth();
    const [searchParams] = useSearchParams();
    if (loading) return null;
    if (user) {
        // must_reset_password short-circuits everything — never let an
        // unrotated password bypass the change-password gate via redirect_to.
        if (user.must_reset_password) return <Navigate to="/change-password" replace />;
        // Phase H.8 — honor the post-auth `redirect_to` so customers landing
        // on /signup or /login from an invite link bounce back to the invite
        // landing page (which auto-claims and forwards to the wizard) rather
        // than getting clobbered onto an empty /customer dashboard.
        const redirect = searchParams.get("redirect_to");
        if (redirect && isSafeRedirect(redirect)) {
            return <Navigate to={redirect} replace />;
        }
        return <Navigate to={user.role === "customer" ? "/customer" : user.role === "manager" ? "/manager" : "/admin"} replace />;
    }
    return children;
}
